import {LitElement, html, css, unsafeCSS} from 'lit';
import {customElement} from 'lit/decorators.js';
import tooltipCss from './styles/tooltip.css?inline';

@customElement('zubie-tooltip')
export class ZubieTooltip extends LitElement {
  static override styles = css`
    ${unsafeCSS(tooltipCss)}
  `;

  private mouseEnterHandler = () => this.show();
  private mouseLeaveHandler = () => this.hide();

  private portal: HTMLElement | null = null;

  private removeTimeout: ReturnType<typeof setTimeout> | null = null;

  private target?: HTMLElement | null;

  override firstUpdated() {
    queueMicrotask(() => {
      // Only set target if one hasn't been set already
      if (!this.target) {
        this.target = <HTMLElement>this.parentNode;
        if (!this.target) {
          // If target doesn't exist, do go any further
          return;
        }
        this.target.addEventListener('mouseenter', this.mouseEnterHandler);
        this.target.addEventListener('mouseleave', this.mouseLeaveHandler);
      }
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Do not use this, it is called immediately once we re-append to the host root
  }

  adjustPosition() {
    requestAnimationFrame(() => {
      const targetRect = this.target?.getBoundingClientRect();

      if (targetRect && this.portal) {
        const height = this.portal.offsetHeight + 4; // 4 = padding
        const width = this.portal.offsetWidth;
        this.portal.style.left = `${
          targetRect.left + targetRect.width / 2 - width / 2
        }px`;
        this.portal.style.top = `${targetRect.top - height}px`;
        this.portal.style.opacity = '1';
        this.portal.style.transform = 'translateY(0)';
      }
    });
  }

  hide() {
    if (this.portal) {
      this.portal.style.opacity = '';
      this.portal.style.transform = '';
      this.removeTimeout = setTimeout(() => {
        this.portal?.remove();
        this.portal = null;
      }, 500);
    }
  }

  show() {
    if (this.removeTimeout) {
      clearTimeout(this.removeTimeout);
    }

    if (this.portal) {
      this.adjustPosition();
      return;
    }

    const hostRoot = this.getRootNode();
    this.portal = this.cloneNode(true) as HTMLDivElement;
    if (hostRoot instanceof ShadowRoot && hostRoot !== this.shadowRoot) {
      hostRoot.appendChild(this.portal);
      this.adjustPosition();
    }
  }

  override render() {
    return html`
      <div class="tooltip" role="tooltip">
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'zubie-tooltip': ZubieTooltip;
  }
}
